// @ts-strict-ignore
import * as React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Tooltip } from '@/atoms/Tooltip/Tooltip';
import { VerticalDivide } from '@/features/Feed/common';
import { useMobileRoutesMatch } from '@/hooks/responsive/useMobileRoutesMatch';
import type { CommonComponentType } from '@/types';
import { getTimeStamp } from '@/utils/getTimeStamp';

export type NewCardTitleType = CommonComponentType & {
  /**
   * unix timestamp
   */
  unix: number;
  /**
   * primary title
   */
  title: React.ReactNode;
  /**
   * card status
   */
  read?: boolean;
  /**
   * sender name
   */
  secondaryTitle?: React.ReactNode;
  /**
   * original/full title
   */
  originalTitle?: React.ReactNode;
  /**
   * rss title cart
   */
  isRssMobileTitle?: boolean;
  /**
   * show entire title
   */
  showEntireTitle?: boolean;
  /**
   * should wrap primary and secondary title
   */
  wrapTitle?: boolean;
};

const Title = styled.h2`
  ${tw`sub-heading-normal-bold break-words whitespace-pre-wrap overflow-hidden text-gray-900 sm:body-normal-bold`}
`;

const NewSecondaryTitle = styled.span`
  ${tw`flex gap-x-2 items-center truncate break-words font-medium`}
`;

const SecondaryTitleContainer = styled.span`
  ${tw`flex gap-x-2 items-center body-xs text-gray-700 w-full`}
`;

export const CardTitleV2 = React.forwardRef<
  HTMLHeadingElement,
  NewCardTitleType
>(
  (
    {
      className = '',
      secondaryTitle,
      style = {},
      title,
      read = false,
      originalTitle,
      isRssMobileTitle,
      showEntireTitle = false,
      wrapTitle,
      unix,
      ...rest
    },
    ref
  ) => {
    const { previewPage } = useMobileRoutesMatch();

    return (
      <div
        ref={ref}
        className={`flex flex-col justify-start items-start ${
          wrapTitle ? '' : 'lg:flex-nowrap '
        }overflow-hidden ${className}`}
        style={style}
        {...rest}
      >
        <Title>
          <Tooltip label={originalTitle || title}>
            <span
              className={
                !showEntireTitle &&
                (previewPage
                  ? 'line-clamp-2'
                  : isRssMobileTitle
                  ? 'line-clamp-3'
                  : read
                  ? 'line-clamp-1 text-gray-700'
                  : 'line-clamp-1')
              }
            >
              {title}
            </span>
          </Tooltip>
        </Title>
        {secondaryTitle || unix ? (
          <SecondaryTitleContainer>
            {secondaryTitle ? (
              <NewSecondaryTitle>{secondaryTitle}</NewSecondaryTitle>
            ) : null}
            {unix && secondaryTitle ? <VerticalDivide size='small' /> : null}
            {unix ? (
              <span className='flex-shrink-0'>{getTimeStamp(unix, true)}</span>
            ) : null}
          </SecondaryTitleContainer>
        ) : null}
      </div>
    );
  }
);

// TODO : remove this after new card title is replaced everywhere

export type CardTitleType = CommonComponentType & {
  /**
   * primary title
   */
  title: React.ReactNode;
  /**
   * sender name
   */
  secondaryTitle?: React.ReactNode;
  /**
   * sender email
   */
  tertiaryTitle?: React.ReactNode;
  /**
   * original/full title
   */
  originalTitle?: React.ReactNode;
  /**
   * rss title cart
   */
  isRssMobileTitle?: boolean;
  /**
   * show entire title
   */
  showEntireTitle?: boolean;
  /**
   * show entire title
   */
  showSecondaryTitle?: boolean;
  /**
   * should wrap primary and secondary title
   */
  wrapTitle?: boolean;
};

const SecondaryTitle = styled.span`
  ${tw`body-xs-bold break-words text-gray-700 truncate flex-shrink overflow-hidden mr-1`}
`;

const TertiaryTitle = styled.span`
  ${tw`body-xs break-words text-gray-700 flex-shrink truncate overflow-hidden`}
`;

export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleType>(
  (
    {
      className = '',
      secondaryTitle,
      style = {},
      tertiaryTitle,
      title,
      originalTitle,
      isRssMobileTitle,
      showSecondaryTitle = true,
      showEntireTitle = false,
      wrapTitle,
      ...rest
    },
    ref
  ) => {
    const { previewPage } = useMobileRoutesMatch();

    return (
      <div
        ref={ref}
        className={`flex flex-wrap ${
          wrapTitle ? '' : 'lg:flex-nowrap'
        } gap-x-2 overflow-hidden sm:items-center ${className}`}
        style={style}
        {...rest}
      >
        <Title>
          <Tooltip label={originalTitle || title}>
            <span
              className={
                !showEntireTitle &&
                (previewPage
                  ? 'line-clamp-2'
                  : isRssMobileTitle
                  ? 'line-clamp-3'
                  : 'line-clamp-1')
              }
            >
              {title}
            </span>
          </Tooltip>
        </Title>
        <div className='flex items-center overflow-hidden truncate'>
          {secondaryTitle && showSecondaryTitle ? (
            <Tooltip
              label={typeof secondaryTitle === 'string' ? secondaryTitle : ''}
            >
              <SecondaryTitle>{secondaryTitle}</SecondaryTitle>
            </Tooltip>
          ) : null}
          {tertiaryTitle ? (
            <TertiaryTitle>{tertiaryTitle}</TertiaryTitle>
          ) : null}
        </div>
      </div>
    );
  }
);
